import {
    IGroupedComboBoxGroup,
    IGroupedComboBoxItem,
    IGroupedComboBoxSubMenuSection,
} from 'grouped-combobox-core';
import { fontInfoItemKey } from '../common/constants';
import { findFontByKey } from '../common/findFontByKey';
import { isMruEntry } from '../common/fontUtils';
import { setFontPinnedState } from '../common/pinning';
import { createPinnedVariantItem, getPinnedItemKey } from '../common/variantSection';
import { findFontIndex } from './findFontIndex';

function findVariantByKey(
    variantKey: string,
    items: IGroupedComboBoxItem[]
): IGroupedComboBoxItem | undefined {
    // Note that variants are stored in a hardcoded order, so we can't do binary search
    return items.find((item) => item.itemKey === variantKey);
}

function isFontInfoSection(section: IGroupedComboBoxSubMenuSection): boolean {
    return section.items.length === 1 && section.items[0].itemKey === fontInfoItemKey;
}

function modifyPinnedSection(
    groups: IGroupedComboBoxGroup[],
    variantKey: string,
    parentKey: string,
    needUpdate: (existingItemIndex: number) => boolean,
    modifyItems: (
        items: IGroupedComboBoxItem[],
        newItem: IGroupedComboBoxItem,
        existingItemIndex: number
    ) => void
): IGroupedComboBoxSubMenuSection[] | null {
    // We can't modify a font variant if we can't find its parent.
    const fontFamilyItem = findFontByKey(parentKey, groups[groups.length - 1].items);

    if (fontFamilyItem?.sections === undefined) {
        return null;
    }

    const currentSections = fontFamilyItem.sections;
    if (currentSections.length === 0) {
        throw new Error('Sections is empty.');
    }

    // Check if the last section is the 'About this font' one.
    const hasFontInfoGroup = isFontInfoSection(currentSections[currentSections.length - 1]);

    let existingItemIndex = -1;

    const expectedNumberOfSections = hasFontInfoGroup ? 3 : 2;
    if (currentSections.length >= expectedNumberOfSections) {
        existingItemIndex = findFontIndex(getPinnedItemKey(variantKey), currentSections[0]);
    }

    if (!needUpdate(existingItemIndex)) {
        return null;
    }

    if (currentSections.length < expectedNumberOfSections) {
        currentSections.unshift({ items: [] });
    }

    const fontItem = findVariantByKey(variantKey, currentSections[1].items);
    if (!fontItem) {
        return null;
    }

    // the Pinned section is always the first one.
    const pinnedSection = currentSections[0];
    modifyItems(pinnedSection.items, fontItem, existingItemIndex);

    const newSections = currentSections.slice();

    // Shallowly change the sections to trigger a re-render
    fontFamilyItem.sections = newSections;

    // Since MRU items don't share an `item` object, we need to update the MRU item's `sections` prop as well.
    // There can be multiple MRU items which share the same font family with the current font variant, because
    // multiple font variants of the same family can be added to MRU.
    groups[0].items.forEach((item) => {
        // 'data' is an instance of `IMruEntry` that generated this IGroupedComboBoxItem object
        const mruItem = item.data;
        if (isMruEntry(mruItem) && mruItem.fontFamilyKey === parentKey) {
            item.sections = newSections;
        }
    });

    return newSections;
}

/**
 * Insert a pinned font variant item into an existing pinned section, maintaining the sort order.
 * @param items - The existing items to insert into. This items will be modified in place.
 * @param pinnedVariantItem - The new item to insert.
 */
export function insertPinnedItem(
    items: IGroupedComboBoxItem[],
    pinnedVariantItem: IGroupedComboBoxItem
): void {
    // If the `order` prop is not defined, we treat it as -1 so that this will be placed
    // before all items which do defined the prop.
    const newItemOrder = pinnedVariantItem.order ?? -1;

    const insertIndex = items.findIndex((item) => {
        const itemOrder = item.order ?? -1;

        // Sort by `order` if they are different.
        if (newItemOrder !== itemOrder) {
            return newItemOrder < itemOrder;
        }

        // otherwise sort by text
        return pinnedVariantItem.text.localeCompare(item.text) < 0;
    });

    if (insertIndex === -1) {
        items.push(pinnedVariantItem);
    } else {
        items.splice(insertIndex, 0, pinnedVariantItem);
    }
}

/**
 * Add the specified font variant to the pinned section.
 * @returns The new sections of the parent font family to which this font variant belongs. If the operation failed, it returns null.
 *
 * @remarks
 *
 * For example, if the font variant is Arial Bold, the returned value is the new sections of the parent Arial font family.
 */
export function addVariantToPinnedSection(
    groups: IGroupedComboBoxGroup[],
    variantKey: string,
    parentKey: string
): IGroupedComboBoxSubMenuSection[] | null {
    return modifyPinnedSection(
        groups,
        variantKey,
        parentKey,
        (existingIndex) => existingIndex === -1,
        (items, newItem) => {
            const pinnedVariantItem = createPinnedVariantItem(newItem);
            setFontPinnedState(pinnedVariantItem, true);

            insertPinnedItem(items, pinnedVariantItem);
        }
    );
}

/**
 * Remove the specified font variant from the pinned section.
 * @returns The new sections of the parent font family to which this font variant belongs. If the operation failed, it returns null.
 *
 * @remarks
 *
 * For example, if the font variant is Arial Bold, the returned value is the new sections of the parent Arial font family.
 */
export function removeVariantFromPinnedSection(
    groups: IGroupedComboBoxGroup[],
    variantKey: string,
    parentKey: string
): IGroupedComboBoxSubMenuSection[] | null {
    return modifyPinnedSection(
        groups,
        variantKey,
        parentKey,
        (existingIndex) => existingIndex !== -1,
        (items, existingItem, existingIndex) => {
            // now mark the font's state as unpinned
            setFontPinnedState(existingItem, false);

            items.splice(existingIndex, 1);
        }
    );
}
